import {
  Stack,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  TextField,
  List,
  ListItemButton,
  ListItemIcon,
  Checkbox,
  ListItem,
  ListItemText,
  DialogActions,
  CircularProgress,
  Chip,
  TablePagination,
} from '@mui/material';
import {
  AppFormikDatePicker,
  AppFormikTextArea,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import { useEffect, useMemo, useState } from 'react';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { ManageEmployeesService } from 'services/ManageEmployeesService';
import DebouncedSearch from 'shared/components/DebouncedSearch';
import { Formik, FormikHelpers } from 'formik';
import { Utils } from 'shared/helpers';
import GroupIcon from '@mui/icons-material/Group';
import { TrainingPorgramsService } from 'services/TrainingProgramService';
import DoneIcon from '@mui/icons-material/DoneOutlined';
import { ITrianingAssignment } from './Assignments';
import { IEmployeeList } from 'modules/settings/management/manage-employees/components/ManageEmployees';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';
import { PaginationActions } from 'state/pagination/Action';

interface ICreateAssignmentProps {
  trainingId: string;
  openDialog: boolean;
  onClose: (reload: boolean) => void;
}

const CreateAssignment: React.FC<ICreateAssignmentProps> = (props) => {
  const dispatch = useDispatch();
  const employeePagination = useSelector(
    (state: IAppState) => state.paginationState.employeeTable
  );

  const { trainingId, openDialog, onClose } = props;
  const [employees, setEmployees] = useState<IEmployeeList[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [departments, setDepartments] = useState<string[]>([]);
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [oldAssignments, setOldAssignments] = useState<ITrianingAssignment[]>(
    []
  );

  // API Calls
  const loadEmployeesAndAssignments = (): void => {
    setIsLoadingRecords(true);
    const queryString = new URLSearchParams({
      offset: String(employeePagination.offset),
      limit: String(employeePagination.pageLimit),
      search: employeePagination.search,
    }).toString();
    const promise = [
      ManageEmployeesService.getAll(queryString),
      TrainingPorgramsService.getAssignments(`${trainingId}`),
    ];
    Promise.all(promise)
      .then((responses) => {
        const { employee, pagination } = responses[0].data.data;
        setEmployees(employee);
        const { data: tData } = responses[1].data;
        setOldAssignments(tData?.records);

        dispatch(
          PaginationActions.setPagination({
            employeeTable: {
              offset: pagination.offset,
              pageLimit: pagination.limit,
              totalRecords: pagination.total,
              totalPages: pagination.totalPages,
            },
          })
        );
      })
      .catch(() => {})
      .finally(() => setIsLoadingRecords(false));
  };

  const createTrainingAssignment = (payload: string, callback: any): void => {
    TrainingPorgramsService.createAssignment(payload)
      .then((response) => {
        const { data } = response.data;
        callback(data);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        callback(false);
      });
  };

  // Instance Methods
  const prepareDepartments = (): void => {
    const tDepartments: string[] = [];
    employees.forEach((e) => {
      if (!departments.includes(e.department)) {
        tDepartments.push(e.department);
      }
    });
    setDepartments(tDepartments);
  };

  const handlePageChange = (_: unknown, page: number): void => {
    dispatch(
      PaginationActions.setPagination({
        employeeTable: {
          offset: page + 1,
        },
      })
    );
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const pageLimit = Number(event.target.value);

    dispatch(
      PaginationActions.setPagination({
        employeeTable: {
          offset: 1,
          pageLimit,
        },
      })
    );
  };

  // UI Actions
  const handleToggle = (item: IEmployeeList): void => {
    if (!oldAssignments.some((o) => o.employee.email === item.email)) {
      const currentIndex = selectedIds.indexOf(item._id);
      const newChecked = [...selectedIds];
      if (currentIndex === -1) {
        newChecked.push(item._id);
      } else {
        newChecked.splice(currentIndex, 1);
      }
      setSelectedIds(newChecked);
    }
  };

  const actionOnSubmit = (
    formValues: any,
    formikHelper: FormikHelpers<any>
  ): void => {
    const payload: any = {
      training_program_id: trainingId,
      assignee_ids: selectedIds,
      status: 'not_started',
      due_date: formValues.dueDate,
      note: formValues.note,
    };

    createTrainingAssignment(payload, (response: any) => {
      formikHelper.setSubmitting(false);
      if (typeof response === 'object') {
        onClose(true);
      }
    });
  };

  // Hooks
  useEffect(() => {
    loadEmployeesAndAssignments();
  }, [
    employeePagination.offset,
    employeePagination.pageLimit,
    employeePagination.search,
  ]);

  useEffect(() => {
    prepareDepartments();
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    // if (searchTerm.trim().length > 0) {
    const search = searchTerm.trim().toLowerCase();
    const tEmp = employees.filter((e) =>
      `${e.name} ${e.email}`.toLowerCase().includes(search)
    );
    if (filterDepartment === 'all') {
      return tEmp;
    }
    return tEmp.filter((e) => e.department === filterDepartment);
    // }
    // return employees;
  }, [searchTerm, employees, filterDepartment]);

  // UI Elements
  return (
    <>
      <Dialog maxWidth="sm" fullWidth open={openDialog}>
        <DialogTitle>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <GroupIcon />
              <Typography variant="h3">Assign Training</Typography>
            </Stack>
            <IconButton onClick={() => onClose(false)}>
              <HighlightOffIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <Formik
          initialValues={{ dueDate: '', note: '' }}
          onSubmit={(formValues, formik) => {
            actionOnSubmit(formValues, formik);
          }}
        >
          {({ handleSubmit, isSubmitting }) => (
            <>
              <DialogContent>
                {isLoadingRecords && (
                  <Box className="empty-content">
                    <CircularProgress />
                  </Box>
                )}
                {!isLoadingRecords && (
                  <>
                    <Typography className="mb-2">Select Employees</Typography>
                    <DebouncedSearch
                      size="medium"
                      delay={100}
                      label=""
                      placeholder="Search Employee"
                      onDebounce={(value) => {
                        setSelectedIds([]);
                        setSearchTerm(value);
                        console.log(value);
                      }}
                    />
                    <Typography className="mb-2">
                      Filter by Department
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Chip
                        size="small"
                        label="All"
                        onClick={() => {
                          setFilterDepartment('all');
                        }}
                        color={
                          filterDepartment === 'all' ? 'primary' : 'default'
                        }
                        deleteIcon={
                          filterDepartment === 'all' ? <DoneIcon /> : <></>
                        }
                      />
                      {departments.map((d) => (
                        <Chip
                          size="small"
                          label={d}
                          deleteIcon={
                            filterDepartment === d ? <DoneIcon /> : <></>
                          }
                          color={filterDepartment === d ? 'primary' : 'default'}
                          onClick={() => {
                            setFilterDepartment(d);
                          }}
                        />
                      ))}
                    </Stack>
                    <List
                      sx={{
                        marginTop: '10px',
                        maxHeight: 200,
                        overflowY: 'auto',
                        border: '1px solid lightgray',
                        borderRadius: '5px',
                      }}
                    >
                      {filteredEmployees.map((item) => (
                        <ListItem key={item._id} disablePadding>
                          <ListItemButton
                            sx={{ padding: '0px 20px' }}
                            role={undefined}
                            onClick={() => handleToggle(item)}
                          >
                            <ListItemIcon>
                              <Checkbox
                                color="primary"
                                edge="start"
                                checked={
                                  selectedIds.includes(item._id) ||
                                  oldAssignments.some(
                                    (o) => o.employee.email === item.email
                                  )
                                }
                                tabIndex={-1}
                                disableRipple
                              />
                            </ListItemIcon>
                            <ListItemText
                              color="primary"
                              primary={
                                <Typography className="black f16 fw-5">
                                  {item.name}
                                  {oldAssignments.some(
                                    (o) => o.employee.email === item.email
                                  ) && (
                                    <small className="ml-2">
                                      Already Assigned
                                    </small>
                                  )}
                                </Typography>
                              }
                              secondary={
                                <Typography className=" f14">
                                  {item.email}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                      {filteredEmployees.length === 0 && (
                        <ListItem disablePadding>
                          <ListItemButton sx={{ padding: '0px 20px' }}>
                            <ListItemText
                              color="primary"
                              primary={
                                <Typography className="black f14">
                                  No Employees
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      )}
                    </List>

                    <Typography className="mb-0 mt-2">
                      Due Date (Optional)
                    </Typography>
                    <AppFormikDatePicker
                      className="mb-0"
                      fullWidth
                      disableToday
                      disablePast
                      name="dueDate"
                      outerLabel
                      placeholder="gg/mm/aaaa"
                      renderInput={(props: any) => <TextField {...props} />}
                    />
                    <Typography className="mb-2">
                      Assignment Notes (Optional)
                    </Typography>
                    <AppFormikTextArea
                      name="note"
                      multiline
                      fullWidth
                      placeholder="Add any additional notes or instruction for the employees."
                      rows={3}
                    />
                  </>
                )}
              </DialogContent>
              <DialogActions>
                <Stack
                  className="mx-4 mb-4"
                  direction="row"
                  spacing={2}
                  alignItems="center"
                >
                  <AppPrimaryButtonOutlined
                    title="Cancel"
                    className="btn-small"
                    onClick={() => {
                      onClose(false);
                    }}
                  />
                  <AppPrimaryButton
                    isLoading={isSubmitting}
                    onClick={handleSubmit}
                    disabled={selectedIds.length === 0}
                    title={`Assign ${selectedIds.length} employees`}
                    className="btn-small"
                  />
                </Stack>
              </DialogActions>
            </>
          )}
        </Formik>
      </Dialog>

      <TablePagination
        component="div"
        count={employeePagination.totalRecords}
        page={employeePagination.offset - 1}
        rowsPerPage={employeePagination.pageLimit}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[10, 20, 50]}
      />
    </>
  );
};

export default CreateAssignment;
